import { AsyncPipe, CommonModule, CurrencyPipe, DecimalPipe, NgOptimizedImage, PercentPipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
  withXhr
} from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  inject,
  ModuleWithProviders,
  NgModule,
  provideAppInitializer
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';

import { SplashScreen } from '@capacitor/splash-screen';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { MaskitoPipe } from '@maskito/angular';
import { EntityDataModule } from '@ngrx/data';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsConfig, StoreDevtoolsModule } from '@ngrx/store-devtools';

import * as echarts from 'echarts';
import * as CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import { NgxEchartsModule } from 'ngx-echarts';

import { CoreRoutingModule } from './core-routing.module';
import { entityConfig } from './entity-metadata';
import { ErrorMessageFromDescriptionInterceptor } from './misc/interceptors/error-message-from-description.interceptor';
import { KeyRefreshInterceptor } from './misc/interceptors/key-refresh.interceptor';
import { LoadingInterceptor } from './misc/interceptors/loading.interceptor';
import { StatusCodeInterceptor } from './misc/interceptors/status-code.interceptor';
import { WalkingFarmCountInterceptor } from './misc/interceptors/walking-farm-count.interceptor';
import { AddressPipe } from './misc/pipes/address.pipe';
import { PercentagePipe } from './misc/pipes/percentage.pipe';
import { SurfacePipe } from './misc/pipes/surface.pipe';
import { TimePipe } from './misc/pipes/time.pipe';
import { UsdCurrencyPipe } from './misc/pipes/usd-currency.pipe';
import { ConfigService } from './misc/services/config.service';
import { DeviceService } from './misc/services/device.service';
import { ErrorService } from './misc/services/error.service';
import { LoggerService } from './misc/services/logger.service';
import { MonetizationService } from './misc/services/monetization.service';
import { StorageService } from './misc/services/storage.service';
import { ThemeService } from './misc/services/theme.service';
import { VoiceAssistantService } from './misc/services/voice-assistant.service';
import { metaReducers, reducers } from './store';
import { CalculatorEffects } from './store/effects/calculator.effects';
import { ConfigEffects } from './store/effects/config.effects';
import { DataProviderEffects } from './store/effects/data-provider.effects';
import { DeviceEffects } from './store/effects/device.effects';
import { LoggerEffects } from './store/effects/logger.effects';
import { MapEffects } from './store/effects/map.effects';
import { MonetizationEffects } from './store/effects/monetization.effects';
import { NetSheetEffects } from './store/effects/net-sheet.effects';
import { PropertyProfileEffects } from './store/effects/property-profile.effects';
import { PushNotificationsEffects } from './store/effects/push-notifications.effects';
import { SettingEffects } from './store/effects/setting.effects';
import { UserEffects } from './store/effects/user.effects';
import { WalkingFarmEffects } from './store/effects/walking-farm.effects';

export const initialLoadFactory =
  (
    configService: ConfigService,
    themeService: ThemeService,
    storageService: StorageService,
    deviceService: DeviceService,
    voiceAssistantService: VoiceAssistantService
  ) =>
  () =>
    new Promise<void>((resolve, reject) => {
      storageService.init().then(
        () => {
          configService.init().subscribe(async (loaded) => {
            if (loaded) {
              themeService.updateTheme();
              await deviceService.init();
              await voiceAssistantService.init();
              await SplashScreen.hide();
              resolve();
            }
          });
        },
        () => {
          reject();
        }
      );
    });

@NgModule({
  declarations: [],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    IonicModule.forRoot({
      mode: 'ios',
      swipeBackEnabled: false
    }),
    IonicStorageModule.forRoot({
      driverOrder: [CordovaSQLiteDriver._driver, Drivers.IndexedDB, Drivers.LocalStorage],
      name: '__smartDirectDB'
    }),
    CoreRoutingModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([
      CalculatorEffects,
      ConfigEffects,
      DataProviderEffects,
      DeviceEffects,
      LoggerEffects,
      MapEffects,
      MonetizationEffects,
      NetSheetEffects,
      PropertyProfileEffects,
      PushNotificationsEffects,
      SettingEffects,
      UserEffects,
      WalkingFarmEffects
    ]),
    EntityDataModule.forRoot(entityConfig),
    NgxEchartsModule.forRoot({ echarts }),
    NgOptimizedImage
  ],
  providers: [
    AddressPipe,
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    MaskitoPipe,
    MonetizationService,
    PercentPipe,
    PercentagePipe,
    SurfacePipe,
    TimePipe,
    UsdCurrencyPipe,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: WalkingFarmCountInterceptor
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorMessageFromDescriptionInterceptor
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: StatusCodeInterceptor
    },
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: KeyRefreshInterceptor
    },
    provideAppInitializer(() => {
      const initializerFn = initialLoadFactory(
        inject(ConfigService),
        inject(ThemeService),
        inject(StorageService),
        inject(DeviceService),
        inject(VoiceAssistantService)
      );
      return initializerFn();
    }),
    { deps: [LoggerService], provide: ErrorHandler, useClass: ErrorService },
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withJsonpSupport())
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CoreModule {
  static forRoot(coreConfig: CoreConfig): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        {
          provide: CoreConfig,
          useValue: coreConfig
        },
        {
          provide: StoreDevtoolsConfig,
          useValue: {
            logOnly: coreConfig.production,
            maxAge: 25
          }
        },
        StoreDevtoolsModule
      ]
    };
  }
}

interface APIConfiguration {
  appId: string;
  appSecret: string;
  baseUrl: string;
}

interface PropertyForceConfiguration {
  baseUrl: string;
}

export class CoreConfig {
  public production = false;
  public container = 'smartdirect';
  public tutorialsUrl = 'https://redshedtechnology.com/sdtutorials';
  public adminPortal: APIConfiguration = {
    appId: '',
    appSecret: '',
    baseUrl: ''
  };
  public report: APIConfiguration = {
    appId: '',
    appSecret: '',
    baseUrl: ''
  };
  public propertyForce: PropertyForceConfiguration = {
    baseUrl: ''
  };
  public loggerHostname = 'smartdirect-mobile-prototype';
  public apiKeys = {
    revenueCat: {
      android: '',
      ios: '',
      web: ''
    },
    stripe: {
      publicKey: ''
    }
  };
  public colors: { primary: string; secondary: string } = {
    primary: '#003b3f',
    secondary: '#b0b1c1'
  };
}
